/**
 * Splits a GPS trail into continuous segments.
 *
 * Breaks on:
 * - `gapBefore` — first breadcrumb after an app-kill resume (Batch 1 clean gap)
 * - `estimated` flipping — inferred Directions gap-fill vs real GPS (Batch 3)
 *
 * Rendering one <Polyline> per segment stops the map drawing a straight
 * "teleport" line across the closed-app gap, and lets estimated segments draw
 * dotted while real GPS stays solid.
 *
 * With no gap/estimated markers this returns a single segment holding the whole
 * trail, so every caller behaves exactly as it did before.
 */
export function splitRouteSegments<
  T extends { gapBefore?: boolean; estimated?: boolean; lat?: number; lng?: number }
>(points: T[] | null | undefined, maxJumpM = 250): T[][] {
  const segments: T[][] = [];
  if (!points || points.length === 0) return segments;
  /* Implied gap: two consecutive stored points further apart than a bus
     plausibly travels between breadcrumbs means the phone recorded nothing
     for a stretch WITHOUT writing a gap marker — drawing through it is the
     "wtf" diagonal cutting across blocks on KCE999Z's evening route
     (Gabriel, 2026-08-24). Lift the pen there too. */
  const mPerDegLat = 111_320;
  const jumped = (a: T, b: T): boolean => {
    if (a.lat == null || a.lng == null || b.lat == null || b.lng == null) return false;
    const mPerDegLng = mPerDegLat * Math.cos((a.lat * Math.PI) / 180);
    const dx = (b.lng - a.lng) * mPerDegLng;
    const dy = (b.lat - a.lat) * mPerDegLat;
    return dx * dx + dy * dy > maxJumpM * maxJumpM;
  };
  let current: T[] = [];
  let currentEstimated = false;
  for (const p of points) {
    const estimated = p.estimated === true;
    if (
      current.length > 0 &&
      (p.gapBefore || estimated !== currentEstimated || jumped(current[current.length - 1], p))
    ) {
      const prevLast = current[current.length - 1];
      segments.push(current);
      /* The gap itself gets DRAWN — a two-point segment flagged estimated, so
         every renderer's existing `seg[0]?.estimated` check makes it dotted.
         An offline stretch or app-kill used to leave a hole between two solid
         lines; now it reads "the bus went from here to here, exact path
         unknown" (Gabriel, 09-05). Point copies, never mutations — these are
         the caller's objects. An estimated-flag flip alone is contiguous: no
         connector. Mirrors route_polyline_segments.dart. */
      if (p.gapBefore || jumped(prevLast, p)) {
        segments.push([
          { ...prevLast, estimated: true },
          { ...p, estimated: true },
        ]);
      }
      current = [];
    }
    if (current.length === 0) currentEstimated = estimated;
    current.push(p);
  }
  if (current.length > 0) segments.push(current);
  return segments;
}

/** Display-only Douglas–Peucker simplification (~metres tolerance).
 *
 *  Live GPS lays a breadcrumb every few seconds; at low speed the scatter makes
 *  the drawn line jitter through buildings ("our live still looks bad" —
 *  Gabriel, 2026-08-24). This trims points that deviate less than `toleranceM`
 *  from the line between their neighbours BEFORE drawing — the stored trail is
 *  untouched, distances and snapping still use every point. First and last
 *  points always survive, so start/end dots and gap handling stay exact. */
export function simplifyForDisplay<
  T extends { lat: number; lng: number }
>(points: T[], toleranceM = 6): T[] {
  if (!points || points.length <= 2) return points ?? [];
  // ~metres per degree at Nairobi's latitude; good enough for a draw tolerance.
  const mPerDegLat = 111_320;
  const mPerDegLng = 111_320 * Math.cos((points[0].lat * Math.PI) / 180);
  const tol2 = toleranceM * toleranceM;
  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  const stack: Array<[number, number]> = [[0, points.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    if (b - a < 2) continue;
    const ax = points[a].lng * mPerDegLng, ay = points[a].lat * mPerDegLat;
    const bx = points[b].lng * mPerDegLng, by = points[b].lat * mPerDegLat;
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    let worst = -1;
    let worstD = tol2;
    for (let i = a + 1; i < b; i++) {
      const px = points[i].lng * mPerDegLng, py = points[i].lat * mPerDegLat;
      let d2: number;
      if (len2 === 0) {
        d2 = (px - ax) ** 2 + (py - ay) ** 2;
      } else {
        const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
        d2 = (px - (ax + t * dx)) ** 2 + (py - (ay + t * dy)) ** 2;
      }
      if (d2 > worstD) { worstD = d2; worst = i; }
    }
    if (worst >= 0) {
      keep[worst] = true;
      stack.push([a, worst], [worst, b]);
    }
  }
  return points.filter((_, i) => keep[i]);
}
